import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { Button, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { betterAuthClient } from '@/libs/better-auth-client';
import { broadcastLogout, clearRedirectCookie } from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';

/**
 * Post-deletion confirmation landing page. Better Auth's delete-user verification link hits the backend
 * callback, which deletes the account (cascading its sessions away) and 302-redirects here. So by the
 * time this page renders the account is already gone — this page only confirms it and finishes cleanup:
 * it drops this browser's now-orphaned Better Auth cookie and broadcasts a logout so any other open
 * Swonkie tab logs out immediately instead of waiting for its next guarded request. Must be a PUBLIC
 * route: the visitor is unauthenticated by design.
 */
export default function AuthAccountDeleted() {
    const { t } = useTranslation();

    useEffect(() => {
        clearRedirectCookie();
        // Sign out any other Swonkie tab still open on this origin, and clear this browser's session.
        broadcastLogout();
        void betterAuthClient.signOut().catch(() => undefined);
    }, []);

    return (
        <AuthSplitLayout>
            <Stack spacing={3} alignItems="center" textAlign="center">
                <CheckCircleRounded color="success" sx={{ fontSize: 56 }} />
                <Stack spacing={1}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.account-deleted.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.account-deleted.message')}
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    href={import.meta.env.VITE_HOMEPAGE_ROOT}
                >
                    {t('auth.account-deleted.back')}
                </Button>
            </Stack>
        </AuthSplitLayout>
    );
}
